// apps/merchant-v3/src/lib/billing/useBillingMode.ts
//
// Phase 6 Wave 12 Task 42 — Derived hook returning the merchant's current
// billing mode (PREPAID / CREDIT / INVOICE) with a memoised getter so
// the TopBar BillingWidget does not re-render on every BillingSummary
// poll tick. The mode is sourced from `useBillingSummary` (
// snake_case `billing_mode`); when the summary is still loading we fall
// back to the cached `useUser().billingMode` so first paint never shows
// an empty chip.
//
// Exports:
//   - BILLING_MODE_PREPAID / CREDIT / INVOICE — string-literal constants
//     used by callers to avoid magic strings (per CLAUDE.md type-strict
//     guideline).
//   - useBillingMode() — returns the resolved mode union.
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-114
// vg-binding: P6.D-245
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { useMemo } from 'react';

import { useBillingSummary } from '@/hooks/useBillingSummary';
import { useUser } from '@/store/user';
import type { BillingMode } from '@/types/user';

export const BILLING_MODE_PREPAID = 'PREPAID' as const;
export const BILLING_MODE_CREDIT = 'CREDIT' as const;
export const BILLING_MODE_INVOICE = 'INVOICE' as const;

/** Resolved billing mode for the current merchant. Defaults to PREPAID
 *  when neither the cached User nor the BillingSummary surface a mode
 *  yet — this matches the BE's default for brand-new merchants. */
export function useBillingMode(): BillingMode {
  const summary = useBillingSummary();
  const user = useUser();

  return useMemo<BillingMode>(() => {
    if (summary?.billing_mode) {
      return summary.billing_mode as BillingMode;
    }
    if (user?.billingMode) {
      return user.billingMode;
    }
    return BILLING_MODE_PREPAID;
  }, [summary?.billing_mode, user?.billingMode]);
}

/** Imperative read — for non-React utilities (rare). Reads the same
 *  underlying hooks, so callers MUST be inside a React render cycle. */
export function useIsInvoiceMode(): boolean {
  return useBillingMode() === BILLING_MODE_INVOICE;
}
